import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import {
  MachineTypeForm,
  type MachineTypeFormValues,
} from '@/components/machine-types/MachineTypeForm';
import { SchemaChangeWarningDialog } from '@/components/machine-types/SchemaChangeWarningDialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { toast } from '@/lib/toast';
import {
  getMachineTypeRequest,
  isSchemaChangeBlocked,
  updateMachineTypeRequest,
} from '@/services/machine-types';
import type {
  MachineTypeDetail,
  SchemaChangeImpact,
  UpdateMachineTypeBody,
} from '@/types/machine-types';

interface PendingSubmit {
  readonly body: UpdateMachineTypeBody;
}

export function MachineTypeEditPage(): JSX.Element {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pendingImpact, setPendingImpact] = useState<SchemaChangeImpact | null>(null);
  const [pendingBody, setPendingBody] = useState<PendingSubmit | null>(null);

  const detailQuery = useQuery<MachineTypeDetail, ApiError>({
    queryKey: ['machine-type', id],
    queryFn: () => getMachineTypeRequest(id),
    enabled: Number.isFinite(id) && id > 0,
    staleTime: 5 * 60_000,
  });

  const updateMutation = useMutation<
    MachineTypeDetail,
    ApiError,
    { body: UpdateMachineTypeBody; force: boolean }
  >({
    mutationFn: ({ body, force }) => updateMachineTypeRequest(id, body, force),
    onSuccess: (updated) => {
      toast.success(`Machine type '${updated.name}' saved`);
      void queryClient.invalidateQueries({ queryKey: ['machine-types'] });
      void queryClient.invalidateQueries({ queryKey: ['machine-type', id] });
      setPendingImpact(null);
      setPendingBody(null);
      void navigate(`/machine-types/${String(id)}`);
    },
    onError: (err) => {
      if (isSchemaChangeBlocked(err)) {
        setPendingImpact(err.impact);
        setErrorMessage(null);
      } else {
        setErrorMessage(err.detail ?? err.message);
        toast.error('Could not update machine type', { description: err.detail ?? err.message });
      }
    },
  });

  if (!permissions.includes('machineTypes.update')) {
    return <Navigate to="/machine-types" replace />;
  }
  if (!Number.isFinite(id) || id <= 0) {
    return <Navigate to="/machine-types" replace />;
  }

  function buildBody(values: MachineTypeFormValues): UpdateMachineTypeBody {
    return {
      name: values.name,
      description: values.description ?? null,
      customFields: values.customFields,
      isActive: values.isActive,
    };
  }

  function handleSubmit(values: MachineTypeFormValues): void {
    setErrorMessage(null);
    const body = buildBody(values);
    setPendingBody({ body });
    updateMutation.mutate({ body, force: false });
  }

  function handleConfirmOverride(): void {
    if (pendingBody !== null) {
      updateMutation.mutate({ body: pendingBody.body, force: true });
    }
  }

  function handleCancelOverride(): void {
    setPendingImpact(null);
    setPendingBody(null);
  }

  const initialValues =
    detailQuery.data !== undefined
      ? {
          name: detailQuery.data.name,
          description: detailQuery.data.description,
          customFields: detailQuery.data.customFields,
          isActive: detailQuery.data.isActive,
        }
      : undefined;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Edit Machine Type</h1>
        <p className="text-muted-foreground">Update machine-type details and schema.</p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>{detailQuery.data?.name ?? 'Machine type'}</CardTitle>
          <CardDescription>
            Schema changes that affect existing machines will require explicit confirmation.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {detailQuery.isLoading && (
            <p className="text-sm text-muted-foreground" data-testid="mt-edit-loading">
              Loading machine type…
            </p>
          )}
          {detailQuery.isError && (
            <p className="text-sm text-destructive" data-testid="mt-edit-error">
              Failed to load machine type.
            </p>
          )}
          {detailQuery.data && initialValues !== undefined && (
            <MachineTypeForm
              mode="edit"
              initialValues={initialValues}
              onSubmit={handleSubmit}
              submitting={updateMutation.isPending}
              errorMessage={errorMessage}
            />
          )}
        </CardContent>
      </Card>

      <SchemaChangeWarningDialog
        open={pendingImpact !== null}
        onOpenChange={(open) => {
          if (!open) handleCancelOverride();
        }}
        impact={pendingImpact}
        onConfirm={handleConfirmOverride}
        onCancel={handleCancelOverride}
        submitting={updateMutation.isPending}
      />
    </div>
  );
}
